import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  prepareReservoDay,
  fetchSalas,
  fetchMesas,
  fetchObjetos,
  createMesa,
  updateMesa,
  deleteMesa,
  createSala,
  updateSala,
  deleteSala,
  createObjeto,
  updateObjeto,
  deleteObjeto,
} from '../services/api.ts';
import {
  listPublicSalas,
  listPublicMesas,
  listPublicObjetos,
} from '../../../../services/publicReservoApi';

// Define types
interface TableShape {
  type: 'square' | 'rectangle' | 'circle';
  width: number;
  height: number;
}

interface Position {
  x: number;
  y: number;
}

const toCenterPosition = (x: number | null | undefined, y: number | null | undefined, width: number, height: number): Position => ({
  x: (x ?? 0) + width / 2,
  y: (y ?? 0) + height / 2
});

const toTopLeftPosition = (position: Position, width: number, height: number): Position => ({
  x: position.x - width / 2,
  y: position.y - height / 2
});

interface Table {
  id: string;
  name: string;
  shape: TableShape;
  texture: string;
  position: Position;
  minCovers: number;
  maxCovers: number;
  rotation: number;
  textColor?: string;
  chairColor?: string;
  color?: string;
  useColor?: boolean; // true = use color, false = use texture
}

interface Element {
  id: string;
  type: 'door' | 'object';
  name: string;
  shape: 'rectangle' | 'square' | 'circle';
  position: Position;
  width: number;
  height: number;
  rotation: number;
  textColor?: string;
  color?: string;
}

interface Floor {
  id: string;
  name: string;
  width: number;
  height: number;
  texture: string;
  tables: Table[];
  elements: Element[];
  modo_layout?: boolean;
  usar_mapa_mesas?: boolean;
  min_pessoas_por_reserva?: number;
  max_pessoas_por_reserva?: number;
  capacidade_maxima?: number;
}

const DEFAULT_PRIVATE_FLOOR = {
  name: 'Salão',
  width: 800,
  height: 600,
  texture: 'wood',
  modo_layout: false,
  tables: [],
  elements: [],
};

interface RestaurantContextType {
  floors: Floor[];
  currentFloor: string;
  setCurrentFloor: (floorId: string) => void;
  addFloor: (floor: Omit<Floor, 'id'>) => Promise<string>;
  updateFloor: (id: string, data: Partial<Floor>) => Promise<void>;
  deleteFloor: (id: string, options?: { force?: boolean }) => Promise<any>;
  addTable: (table: Omit<Table, 'id'>) => Promise<boolean>;
  updateTable: (id: string, data: Partial<Table>) => Promise<boolean>;
  deleteTable: (id: string) => Promise<boolean>;
  addElement: (element: Omit<Element, 'id'>) => Promise<void>;
  updateElement: (id: string, data: Partial<Element>) => Promise<void>;
  deleteElement: (id: string) => Promise<void>;
  saveFloorPlan: () => Promise<void>;
  createDefaultFloor: () => Promise<string>;
}

const RestaurantContext = createContext<RestaurantContextType | undefined>(undefined);

export const RestaurantProvider: React.FC<{ children: React.ReactNode; isPublic?: boolean; slug?: string }> = ({ children, isPublic = false, slug }) => {
  const [floors, setFloors] = useState<Floor[]>([]);
  const [currentFloor, setCurrentFloor] = useState('');
  const [privateDataReady, setPrivateDataReady] = useState(false);

  useEffect(() => {
    let loading = false;
    let disposed = false;
    let preparedDay = '';
    const dayFormatter = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo' });
    const loadData = async () => {
      if (loading) return;
      loading = true;
      try {
        let salas, mesas, objetos;

        if (isPublic && slug) {
          // Load public data
          [salas, mesas, objetos] = await Promise.all([
            listPublicSalas(slug),
            listPublicMesas(slug),
            listPublicObjetos(slug)
          ]);
        } else {
          try {
            const requestedDay = dayFormatter.format(new Date());
            await prepareReservoDay();
            preparedDay = requestedDay;
          } catch (error) {
            console.error('Erro ao preparar o dia do Reservo:', error);
          }
          // Load private data
          [salas, mesas, objetos] = await Promise.all([
            fetchSalas(),
            fetchMesas(),
            fetchObjetos()
          ]);
        }

        const salasList = Array.isArray(salas) ? salas : [];
        const mesasList = Array.isArray(mesas) ? mesas : [];
        const objetosList = Array.isArray(objetos) ? objetos : [];

        const floorsFromAPI: Floor[] = salasList.map((sala: any) => {
          const mesasDaSala = mesasList
            .filter((m: any) => m.sala_id === sala.id)
            .map((m: any) => {
              const width = m.largura ?? 0;
              const height = m.altura ?? 0;
              return {
                id: m.id,
                name: m.nome,
                shape: { type: m.shape, width, height },
                texture: m.textura,
                position: toCenterPosition(m.x, m.y, width, height),
                minCovers: m.min,
                maxCovers: m.max,
                rotation: m.rotacao || 0,
                textColor: m.cor_texto || '#000000',
                chairColor: m.cor_cadeira || '#8B4513',
                color: m.cor_mesa || '#8B4513',
                useColor: m.usar_cor || false
              };
            });

          const elementosDaSala = objetosList
            .filter((e: any) => e.sala_id === sala.id)
            .map((e: any) => {
              const width = e.largura ?? 0;
              const height = e.altura ?? 0;
              return {
                id: e.id,
                type: 'object' as const,
                name: e.nome,
                shape: e.shape,
                position: toCenterPosition(e.x, e.y, width, height),
                width,
                height,
                rotation: e.rotacao || 0,
                textColor: e.cor_texto || '#000000',
                color: e.cor || undefined
              };
            });

          return {
            id: sala.id,
            name: sala.nome,
            width: sala.largura,
            height: sala.altura,
            texture: sala.textura,
            modo_layout: sala.modo_layout,
            usar_mapa_mesas: sala.usar_mapa_mesas,
            min_pessoas_por_reserva: sala.min_pessoas_por_reserva,
            max_pessoas_por_reserva: sala.max_pessoas_por_reserva,
            capacidade_maxima: sala.capacidade_maxima,
            tables: mesasDaSala,
            elements: elementosDaSala
          };
        });

        if (!disposed) {
          setFloors(floorsFromAPI);
          if (floorsFromAPI.length > 0) {
            setCurrentFloor((previous) => floorsFromAPI.some((floor) => floor.id === previous)
              ? previous : floorsFromAPI[0].id);
          }
        }
      } catch (err) {
        console.error('Erro ao carregar dados do Reservo:', err);
      } finally {
        loading = false;
        if (!disposed) setPrivateDataReady(true);
      }
    };

    if (isPublic && !slug) {
      console.error('Slug is required for public mode');
      return;
    }

    void loadData();
    const handleReturn = () => {
      if (!isPublic && document.visibilityState === 'visible'
        && preparedDay !== dayFormatter.format(new Date())) {
        void loadData();
      }
    };
    if (!isPublic) {
      window.addEventListener('focus', handleReturn);
      document.addEventListener('visibilitychange', handleReturn);
    }
    return () => {
      disposed = true;
      window.removeEventListener('focus', handleReturn);
      document.removeEventListener('visibilitychange', handleReturn);
    };
  }, [isPublic, slug]);

  const addTable = async (table: Omit<Table, 'id'>): Promise<boolean> => {
    const floorId = currentFloor;
    try {
      const { x, y } = toTopLeftPosition(table.position, table.shape.width, table.shape.height);
      const response = await createMesa({
        sala_id: floorId,
        nome: table.name,
        shape: table.shape.type,
        textura: table.texture,
        altura: table.shape.height,
        largura: table.shape.width,
        min: table.minCovers,
        max: table.maxCovers,
        x,
        y,
        rotacao: table.rotation,
        cor_texto: table.textColor || '#000000',
        cor_cadeira: table.chairColor || '#8B4513',
        cor_mesa: table.color || '#8B4513',
        usar_cor: table.useColor || false
      });
      const newTable = { ...table, id: response.id };
      setFloors(prev => prev.map(floor =>
        floor.id === floorId
          ? { ...floor, tables: [...floor.tables, newTable] }
          : floor
      ));
      return true;
    } catch (err) {
      console.error('Erro ao adicionar mesa:', err);
      return false;
    }
  };

  const addFloor = async (floor: Omit<Floor, 'id'>): Promise<string> => {
    const response = await createSala(floor);

    const newFloor: Floor = {
      ...floor,
      id: response.id,
      usar_mapa_mesas: response.usar_mapa_mesas ?? floor.usar_mapa_mesas,
      tables: floor.tables ?? [],
      elements: floor.elements ?? []
    } as Floor;

    setFloors(prev => [...prev, newFloor]);
    return response.id;
  };

  const createDefaultFloor = async (): Promise<string> => {
    const floorId = await addFloor(DEFAULT_PRIVATE_FLOOR as Omit<Floor, 'id'>);
    setCurrentFloor(floorId);
    return floorId;
  };

  const updateFloor = async (id: string, data: Partial<Floor>) => {
    await updateSala(id, data);
    setFloors(prev =>
      prev.map(floor => (floor.id === id ? { ...floor, ...data } : floor))
    );
  };

  const deleteFloor = async (id: string, options?: { force?: boolean }) => {
    const response = await deleteSala(id, options);

    const updatedFloors = floors.filter(floor => floor.id !== id);
    setFloors(updatedFloors);

    if (currentFloor === id) {
      setCurrentFloor(updatedFloors.length ? updatedFloors[0].id : '');
    }

    return response?.data ?? null;
  };

  const addElement = async (element: any): Promise<void> => {
    const { x, y } = toTopLeftPosition(element.position, element.width, element.height);
    const response = await createObjeto({
      ...element,
      sala_id: currentFloor,
      cor: element.color,
      cor_texto: element.textColor,
      textura: null,
      position: { x, y }
    });
    const currentFloorObj = floors.find(floor => floor.id === currentFloor);
    if (!currentFloorObj) return;
    const newElement = { ...element, id: response.id };
    updateFloor(currentFloor, {
      elements: [...currentFloorObj.elements, newElement]
    });
  };

  const updateElement = async (id: string, data: any): Promise<void> => {
    const currentFloorObj = floors.find(floor => floor.id === currentFloor);
    if (!currentFloorObj) return;
    const elementToUpdate = currentFloorObj.elements.find(el => el.id === id);
    if (!elementToUpdate) return;

    const updatedElement = { ...elementToUpdate, ...data };
    const { x, y } = toTopLeftPosition(updatedElement.position, updatedElement.width, updatedElement.height);

    await updateObjeto(id, {
      nome: updatedElement.name,
      shape: updatedElement.shape,
      textura: null,
      cor: updatedElement.color,
      height: updatedElement.height,
      width: updatedElement.width,
      position: { x, y },
      rotacao: updatedElement.rotation
    });

    const updatedElements = currentFloorObj.elements.map(el =>
      el.id === id ? updatedElement : el
    );
    updateFloor(currentFloor, { elements: updatedElements });
  };

  const deleteElement = async (id: string): Promise<void> => {
    await deleteObjeto(id);
    const currentFloorObj = floors.find(floor => floor.id === currentFloor);
    if (!currentFloorObj) return;
    const updatedElements = currentFloorObj.elements.filter(el => el.id !== id);
    updateFloor(currentFloor, { elements: updatedElements });
  };

  const saveFloorPlan = async (): Promise<void> => {
    // lógica para salvar planta completa, se necessário
  };


  const updateTable = async (id: string, data: Partial<Table>): Promise<boolean> => {
    try {
      const currentFloorObj = floors.find(floor => floor.id === currentFloor);
      if (!currentFloorObj) return false;
      const tableToUpdate = currentFloorObj.tables.find(t => t.id === id);
      if (!tableToUpdate) return false;

      const targetShape = data.shape ?? tableToUpdate.shape;
      const targetPosition = data.position ?? tableToUpdate.position;
      const { x, y } = toTopLeftPosition(targetPosition, targetShape.width, targetShape.height);

      await updateMesa(id, {
        nome: data.name ?? tableToUpdate.name,
        shape: targetShape.type,
        textura: data.texture ?? tableToUpdate.texture,
        altura: targetShape.height,
        largura: targetShape.width,
        min: data.minCovers ?? tableToUpdate.minCovers,
        max: data.maxCovers ?? tableToUpdate.maxCovers,
        x,
        y,
        rotacao: data.rotation ?? tableToUpdate.rotation,
        cor_texto: data.textColor ?? tableToUpdate.textColor,
        cor_cadeira: data.chairColor ?? tableToUpdate.chairColor,
        cor_mesa: data.color ?? tableToUpdate.color,
        usar_cor: data.useColor ?? tableToUpdate.useColor
      });

      const updatedTables = currentFloorObj.tables.map(table =>
        table.id === id ? { ...table, ...data } : table
      );
      setFloors(prev =>
        prev.map(floor => (floor.id === currentFloor ? { ...floor, tables: updatedTables } : floor))
      );
      return true;
    } catch (err) {
      console.error('Erro ao atualizar mesa:', err);
      return false;
    }
  };

  const deleteTable = async (id: string): Promise<boolean> => {
    const floorId = currentFloor;
    try {
      await deleteMesa(id);
      setFloors(prev => prev.map(floor =>
        floor.id === floorId
          ? { ...floor, tables: floor.tables.filter(table => table.id !== id) }
          : floor
      ));
      return true;
    } catch (err) {
      console.error('Erro ao deletar mesa:', err);
      return false;
    }
  };

  // Outros métodos permanecem iguais...

  const value = {
    floors,
    currentFloor,
    setCurrentFloor,
    addFloor,
    updateFloor,
    deleteFloor,
    addTable,
    updateTable,
    deleteTable,
    addElement,
    updateElement,
    deleteElement,
    saveFloorPlan,
    createDefaultFloor
  };

  return (
    <RestaurantContext.Provider value={value}>
      {isPublic || privateDataReady ? children : <div role="status">Carregando Reservo...</div>}
    </RestaurantContext.Provider>
  );
};

export const useRestaurant = () => {
  const context = useContext(RestaurantContext);
  if (context === undefined) {
    throw new Error('useRestaurant must be used within a RestaurantProvider');
  }
  return context;
};
